/**
 * Activity Feed Component
 * Real-time activity monitoring with auto-refresh and color-coded severity
 */

import { useActivityFeed } from '../api/client';
import { useEffect, useRef } from 'react';

// Activity type icons mapping
const getActivityIcon = (type: string): string => {
  const icons: Record<string, string> = {
    SCAN: '🔍',
    EVALUATION: '🎯',
    SIGNAL: '📈',
    TRADE: '💵',
    EXIT: '🚪',
    ERROR: '⚠️',
    BREAKER: '🛡️',
    INFO: 'ℹ️',
  };
  return icons[type] || '•';
};

// Severity color mapping
const getSeverityColors = (severity: string): { bg: string; text: string; border: string } => {
  switch (severity) {
    case 'INFO':
      return {
        bg: 'rgba(59, 130, 246, 0.1)',
        text: 'var(--accent-blue)',
        border: 'var(--accent-blue)',
      };
    case 'WARNING':
      return {
        bg: 'rgba(245, 158, 11, 0.1)',
        text: 'var(--accent-yellow)',
        border: 'var(--accent-yellow)',
      };
    case 'ERROR':
      return {
        bg: 'rgba(239, 68, 68, 0.1)',
        text: 'var(--accent-red)',
        border: 'var(--accent-red)',
      };
    case 'CRITICAL':
      return {
        bg: 'rgba(239, 68, 68, 0.2)',
        text: 'var(--accent-red)',
        border: 'var(--accent-red)',
      };
    default:
      return {
        bg: 'rgba(255, 255, 255, 0.02)',
        text: 'var(--text-secondary)',
        border: 'var(--border-color)',
      };
  }
};

// Format timestamp
const formatTimestamp = (timestamp: string): string => {
  // Convert database format "2025-10-17 18:05:26" to ISO format "2025-10-17T18:05:26"
  const isoTimestamp = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T');
  const date = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);

  if (diffSecs < 60) {
    return `${diffSecs}s ago`;
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
};

// Format metadata value for display
const formatMetadataValue = (value: any): string => {
  if (value === null || value === undefined) {
    return 'null';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }
  if (typeof value === 'object') {
    // For objects, show a compact summary of key metrics
    const keys = Object.keys(value);
    if (keys.length === 0) return '{}';
    if (keys.length <= 3) {
      // Show small objects inline
      return JSON.stringify(value);
    }
    return `{${keys.length} keys}`;
  }
  return String(value);
};

export function ActivityFeed() {
  const { data, isLoading, error } = useActivityFeed(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousCountRef = useRef<number>(0);

  // Auto-scroll to top when new activities arrive
  useEffect(() => {
    const activities = data?.activityFeed || [];
    if (activities.length > previousCountRef.current && containerRef.current) {
      // Only scroll if user is near the top (within 100px)
      if (containerRef.current.scrollTop < 100) {
        containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    previousCountRef.current = activities.length;
  }, [data?.activityFeed]);

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Activity Feed</h2>
        </div>
        <div className="loading">Loading activity feed...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Activity Feed</h2>
        </div>
        <div className="error">Error loading activity feed: {error.message}</div>
      </div>
    );
  }

  const activities = data?.activityFeed || [];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Activity Feed</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="card-badge badge-success">{activities.length} Events</span>
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'var(--accent-green)',
              boxShadow: '0 0 8px var(--accent-green)',
              animation: 'pulse 2s infinite',
            }}
          />
        </div>
      </div>

      {activities.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <p>No recent activity</p>
        </div>
      ) : (
        <div
          ref={containerRef}
          style={{
            maxHeight: '600px',
            overflowY: 'auto',
            marginTop: '1rem',
            paddingRight: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {activities.map((activity) => {
              const colors = getSeverityColors(activity.severity);
              const icon = getActivityIcon(activity.activityType);
              const isCritical = activity.severity === 'CRITICAL';

              return (
                <div
                  key={activity.id}
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    padding: '0.875rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bg,
                    transition: 'all 0.2s ease',
                    fontWeight: isCritical ? 700 : 400,
                  }}
                  className="activity-item"
                >
                  {/* Icon */}
                  <div
                    style={{
                      fontSize: '1.25rem',
                      flexShrink: 0,
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                    }}
                  >
                    {icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Header row */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.25rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* Activity type badge */}
                      <span
                        style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.625rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          background: colors.text,
                          color: 'white',
                        }}
                      >
                        {activity.activityType}
                      </span>

                      {/* Symbol if present */}
                      {activity.symbol && (
                        <span
                          style={{
                            padding: '0.125rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'var(--text-primary)',
                          }}
                        >
                          {activity.symbol}
                        </span>
                      )}

                      {/* Timestamp */}
                      <span
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          marginLeft: 'auto',
                        }}
                      >
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>

                    {/* Message */}
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: isCritical ? colors.text : 'var(--text-primary)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {activity.message}
                    </div>

                    {/* Metadata (if present and useful) */}
                    {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                      <div
                        style={{
                          marginTop: '0.5rem',
                          fontSize: '0.75rem',
                          color: 'var(--text-secondary)',
                          fontFamily: 'monospace',
                          background: 'rgba(0, 0, 0, 0.2)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          maxHeight: '80px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                        }}
                        title={JSON.stringify(activity.metadata, null, 2)}
                      >
                        {Object.entries(activity.metadata).map(([key, value]) => (
                          <div key={key} style={{ marginBottom: '0.125rem' }}>
                            <span style={{ color: 'var(--accent-blue)', fontWeight: 600 }}>
                              {key}:
                            </span>{' '}
                            {formatMetadataValue(value)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Scrollbar styling and animation */}
      <style>{`
        .activity-item:hover {
          transform: translateX(2px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Custom scrollbar styling */
        div[style*="overflowY: auto"]::-webkit-scrollbar {
          width: 6px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 3px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }

        div[style*="overflowY: auto"]::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      `}</style>
    </div>
  );
}
